/* IntraoralScansOverviewCard — iter97.78
 *
 * Smart 3-state Intraoral Scans card for the Patient Profile Overview
 * tab. States derived from the props passed in:
 *
 *   • "none"      — no scans on file AND no upcoming scan visit
 *   • "scheduled" — at least one scheduled scan visit, no scans yet
 *   • "has"       — at least one scan file on this patient's cases
 *
 * Each state has its own visual treatment + primary action.
 *
 * Props:
 *   scans:    array — from GET /doctor/patients/{id}/scans
 *   visits:   array — from GET /doctor/patients/{id}/scan-visits
 *   onRequestVisit(): void — opens the request modal
 *   onViewAll():      void — switches to the Intraoral Scans tab
 */
import React from "react";
import { Button } from "@/components/ui/button";
import {
    Scan, CalendarPlus, ArrowRight, Cube, Clock, PencilSimple, X as XIcon,
} from "@phosphor-icons/react";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

function fmtTime(t) {
    if (!t) return "";
    const h = Number(t.split(":")[0]);
    const ampm = h >= 12 ? "PM" : "AM";
    const twelveHr = h % 12 || 12;
    return `${String(twelveHr).padStart(2, "0")}:${t.split(":")[1] || "00"} ${ampm}`;
}

function kindLabel(k) {
    return {
        scan_upper: "Upper arch",
        scan_lower: "Lower arch",
        scan_bite: "Bite registration",
    }[k] || k;
}

export default function IntraoralScansOverviewCard({ scans = [], visits = [], onRequestVisit, onViewAll }) {
    const upcomingVisit = visits.find((v) => v.status === "scheduled");
    const hasScans = scans.length > 0;
    const state = hasScans ? "has" : upcomingVisit ? "scheduled" : "none";

    // ── STATE A: No scans, no visit ──
    //
    // iter97.96 — Rendered as a prominent primary-teal banner that mirrors
    // the amber "Submit a new case to planning" CTA layout (icon disc on
    // the left, title + body in the middle, pill button on the right).
    // Intraoral scans are the prerequisite for case planning, so the
    // banner now sits ABOVE the Submit-case prompt and carries equal
    // visual weight. The colour is intentionally different (primary vs.
    // amber) so the two prompts read as siblings, not duplicates.
    if (state === "none") {
        return (
            <div
                className="relative rounded-2xl border border-primary/30 bg-primary/5 p-5"
                data-testid="overview-scans-card"
            >
                <div className="flex items-start gap-4" data-testid="overview-scans-state-none">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-white border border-primary/20 grid place-items-center text-primary">
                        <Scan size={18} weight="bold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-base font-bold leading-tight">Capture intraoral scans</div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Request an intraoral scan visit. Our technician will upload the scans on-site and link them to the relevant case automatically.
                        </p>
                    </div>
                    <div className="shrink-0">
                        <Button
                            size="sm"
                            onClick={onRequestVisit}
                            className="rounded-full px-4 bg-primary hover:bg-primary/90 text-white"
                            data-testid="overview-scans-request-btn"
                        >
                            Request scan visit <ArrowRight size={12} weight="bold" className="ml-1.5" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border-[0.5px] border-border rounded-[12px] px-5 py-4" data-testid="overview-scans-card">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-[8px] grid place-items-center bg-primary/10 text-primary">
                        <Scan size={14} weight="duotone" />
                    </span>
                    <span className="text-[13px] font-semibold">Intraoral Scans</span>
                </div>
                {state === "has" && onViewAll && (
                    <button
                        type="button"
                        onClick={onViewAll}
                        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-primary hover:underline"
                        data-testid="overview-scans-view-all"
                    >
                        View all scans
                        <ArrowRight size={11} weight="bold" />
                    </button>
                )}
            </div>

            {/* ── STATE B: Visit scheduled ── */}
            {state === "scheduled" && (
                <div data-testid="overview-scans-state-scheduled">
                    <div className="flex items-start gap-2.5 p-3 rounded-[10px] bg-amber-50 border-[0.5px] border-amber-300 mb-3">
                        <Clock size={15} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold text-amber-900 leading-snug">
                                Scan visit scheduled · {fmtDate(upcomingVisit.date)} · {fmtTime(upcomingVisit.time)}
                            </div>
                            <p className="text-[11.5px] text-amber-800 leading-snug mt-1">
                                Scans will be uploaded by the technician after the visit and linked to the case automatically.
                            </p>
                            {upcomingVisit.technician_name && (
                                <p className="text-[11px] text-amber-800/80 mt-1">
                                    Assigned: <span className="font-medium">{upcomingVisit.technician_name}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── STATE C: Has scans ── */}
            {state === "has" && (
                <div data-testid="overview-scans-state-has">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">
                        Latest scans
                    </div>
                    <div>
                        {scans.slice(0, 3).map((s, idx) => (
                            <div
                                key={s.file_id || idx}
                                className="flex items-center justify-between py-2 border-t-[0.5px] border-border first:border-t-[0.5px]"
                                data-testid={`overview-scans-row-${idx}`}
                            >
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="w-7 h-7 rounded-full grid place-items-center bg-emerald-100 text-emerald-700 shrink-0">
                                        <Cube size={13} weight="duotone" />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-[12.5px] font-medium truncate">
                                            {kindLabel(s.kind)}{s.mime ? ` · ${s.mime.split("/").pop().toUpperCase()}` : ""}
                                        </div>
                                        <div className="text-[10.5px] text-muted-foreground">
                                            Uploaded {fmtDate(s.uploaded_at)}
                                        </div>
                                    </div>
                                </div>
                                {s.case_code && (
                                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full shrink-0 ml-2">
                                        {s.case_code}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="border-t-[0.5px] border-border/60 mt-2 pt-2.5 flex items-center justify-between flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onRequestVisit}
                            className="gap-1.5"
                            data-testid="overview-scans-request-btn"
                        >
                            <CalendarPlus size={13} weight="bold" />
                            Request new scan visit
                        </Button>
                        {onViewAll && (
                            <button
                                type="button"
                                onClick={onViewAll}
                                className="inline-flex items-center gap-1 text-[11.5px] font-medium text-primary hover:underline"
                            >
                                View all scans
                                <ArrowRight size={11} weight="bold" />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
